import type { PersonaScenario } from './simulatorTypes'

// The guided personas for the simulator playback (AI-42). All three are kept
// here as data — see ACTIVE_PERSONA_SCENARIOS below for which ones the wizard
// actually walks through.
//
// - profile.name_key / profile.descriptor_key / opener are i18n keys RELATIVE to
//   `recovery_ai.sandbox.personas` — resolve with
//   buildTranslate('recovery_ai.sandbox.personas'). They are the user-facing
//   framing copy, so they go through i18n per CLAUDE.md.
// - order_context is literal sample DATA (fake order details) — it is demo data
//   the simulator feeds the agent, not product chrome, so it is not i18n-routed.
export const PERSONA_SCENARIOS: PersonaScenario[] = [
  {
    persona_id: 'first_time_delivery',
    profile: {
      name_key: 'first_time_delivery.name',
      descriptor_key: 'first_time_delivery.descriptor',
    },
    opener: 'first_time_delivery.opener',
    expected_outcome: 'reward_grant',
    order_context: {
      scenario_id: 'first_time_delivery',
      scenario_name: 'First-time customer — missing item',
      scenario_description:
        'A brand-new customer placed their first order and the fries never made it into the bag.',
      order: {
        order_id: 'ORD-PER-002',
        location_name: 'Midtown Location',
        order_date: '2026-05-21',
        order_total_cents: 3543,
        items: [
          {
            id: 'per-002-1',
            name: 'Sandwich',
            quantity: 1,
            price_cents: 2895,
          },
          {
            id: 'per-002-2',
            name: 'Fries',
            quantity: 1,
            price_cents: 349,
          },
          {
            id: 'per-002-3',
            name: 'Fountain Drink',
            quantity: 1,
            price_cents: 299,
          },
        ],
        delivery_method: 'delivery',
        customer_first_name: 'Priya',
      },
    },
    // v2 dossier (thanx-ai app/lib/thanx_ai/ai/dossier/signals/*.rb). A
    // first-time customer has no purchase-frequency / spend history to band
    // against, so only lifecycle + this order's value are populated — every
    // other signal correctly renders "Unknown — not available for this case."
    //   - lifecycle_stage.rb GLOSSES["initial_purchase"] -> "This is the
    //     customer's very first order with this merchant."
    //   - order_value.rb band "typical" -> "This order was about typical for
    //     this merchant."
    dossier: {
      version: 2,
      relationship: {
        lifecycle_stage: 'initial_purchase',
        order_value: { band: 'typical' },
      },
    },
  },
  {
    persona_id: 'vip_missing_item',
    profile: {
      name_key: 'vip_missing_item.name',
      descriptor_key: 'vip_missing_item.descriptor',
    },
    opener: 'vip_missing_item.opener',
    expected_outcome: 'reward_grant',
    order_context: {
      scenario_id: 'vip_missing_item',
      scenario_name: 'VIP — missing item',
      scenario_description:
        'A long-standing loyalty member ordered two Burrito Bowls to share with a friend, but only one arrived.',
      order: {
        order_id: 'ORD-PER-001',
        location_name: 'Downtown Location',
        order_date: '2026-05-20',
        order_total_cents: 3199,
        items: [
          {
            id: 'per-001-1',
            name: 'Burrito Bowl',
            quantity: 2,
            price_cents: 1250,
          },
          {
            id: 'per-001-2',
            name: 'Chips & Queso',
            quantity: 1,
            price_cents: 400,
          },
          {
            id: 'per-001-3',
            name: 'Fountain Drink',
            quantity: 1,
            price_cents: 299,
          },
        ],
        delivery_method: 'delivery',
        customer_first_name: 'Jordan',
      },
    },
    // v2 dossier. reward_behavior / complaint_history omitted — Unknown is
    // acceptable for now (not authored by any scenario yet).
    //   - lifecycle_stage.rb GLOSSES["engaged"] -> "An active, established
    //     customer, ordering regularly and recently."
    //   - purchase_frequency.rb band "above_typical" ->
    //     comparative("more often than") -> "Orders more often than a typical
    //     regular at this merchant, based on the last 90 days." (ticket asked
    //     for "more frequently" — the signal's actual wording is "more often";
    //     "above_typical" is still the correct/closest band, flagged in the
    //     report as a wording deviation, not a band mismatch)
    //   - order_value.rb band "typical" -> "This order was about typical for
    //     this merchant."
    //   - recent_ltv.rb band "outlier" -> PHRASES["outlier"] "spent
    //     dramatically more than" -> "Has spent dramatically more than a
    //     typical regular at this merchant over the last 90 days."
    //   - lifetime_ltv.rb band "outlier" (reuses RecentLtv::PHRASES) -> "Has
    //     spent dramatically more than a typical regular at this merchant,
    //     over their entire time as a customer."
    dossier: {
      version: 2,
      relationship: {
        lifecycle_stage: 'engaged',
        purchase_frequency: { band: 'above_typical' },
        order_value: { band: 'typical' },
        recent_ltv: { band: 'outlier' },
        lifetime_ltv: { band: 'outlier' },
      },
    },
  },
  {
    persona_id: 'repeat_complainer',
    profile: {
      name_key: 'repeat_complainer.name',
      descriptor_key: 'repeat_complainer.descriptor',
    },
    opener: 'repeat_complainer.opener',
    expected_outcome: 'route_to_team',
    order_context: {
      scenario_id: 'repeat_complainer',
      scenario_name: 'Repeat complainer — escalate',
      scenario_description:
        'A customer who has filed several complaints this month is reporting another issue.',
      order: {
        order_id: 'ORD-PER-003',
        location_name: 'Uptown Location',
        order_date: '2026-05-22',
        order_total_cents: 1850,
        items: [
          {
            id: 'per-003-1',
            name: 'Chicken Burrito',
            quantity: 1,
            price_cents: 1050,
          },
          {
            id: 'per-003-2',
            name: 'Chips & Salsa',
            quantity: 1,
            price_cents: 500,
          },
          {
            id: 'per-003-3',
            name: 'Horchata',
            quantity: 1,
            price_cents: 300,
          },
        ],
        delivery_method: 'pickup',
        customer_first_name: 'Marcus',
      },
    },
  },
]

// The wizard's guided playback shows only these — scenario 3 (repeat_complainer)
// is intentionally HIDDEN pending a replacement scenario (future PR). Its data,
// locale copy, canned messages, and PersonaId union member all stay untouched
// (and stay covered by their own tests) so the swap-in is a one-line diff here
// when the replacement lands. usePersonaPlayback (the wizard's guided-phase
// state machine) reads this list, not PERSONA_SCENARIOS directly, so the
// progress dots / "Scenario x/y" chip / free-phase handoff all derive from
// its length automatically.
export const ACTIVE_PERSONA_SCENARIOS: PersonaScenario[] =
  PERSONA_SCENARIOS.filter(
    scenario => scenario.persona_id !== 'repeat_complainer'
  )
